import { format } from "date-fns";
import { Link, useParams } from "react-router-dom";
import { ALL_REGIONS_CODE, regionPath } from "@/features/regions/constants";
import { useState } from "react";
import { MoreHorizontal, Pencil, Check, X, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BundleExpirationBadge } from "@/components/common/bundle-expiration-badge";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";

import { ExtendBundleDialog } from "./extend-bundle-dialog";
import { AssessmentOutcomeDialog } from "./assessment-outcome-dialog";
import { DivaReportDialog } from "./diva-report-dialog";
import GiftedByInfo from "./gifted-by-info";
import { bundleStatusToVariant } from "@/helpers/bundle-status-to-variant";
import { buildV2ProductBookingLink } from "@/lib/dashboard-url";
import { isV2ProductsEnabled } from "@/lib/v2-flag";
import { getExceptionScheduleLink } from "@/helpers/get-exp-schedule-link";
import {
  getMensHealthFollowUpLinks,
  getMensHealthTherapyLink,
  isMensHealthBundle,
  isMensHealthTconsultBundle,
} from "@/helpers/mens-health-schedule";
import {
  getWomensHealthFollowUpLinks,
  isWomensHealthBundle,
} from "@/helpers/womens-health-schedule";
import {
  getUsageLinkCopyNotAllowedMessage,
  isUsageLinkCopyAllowed,
} from "@/helpers/is-usage-link-copy-allowed";
import { useUpdateBundleUsageStatus } from "../hooks/use-update-bundle-usage-status";
import { useUpdateBundleUsage } from "../hooks/use-update-bundle";
import { useUpdateUsageUser } from "../hooks/use-update-usage-user";
import { useActiveBundles } from "@/features/coupons/hooks/use-active-bundles";
import { BundleCombobox } from "./bundle-combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExceptionBadge } from "@/features/exception-logs/ui/exception-badge";

interface UsageCardProps {
  bundle: any;
  user: any;
  id: string;
  updateUsageException: (usageId: string) => void;
  addFreeSession: (
    usage_id: string,
    userId: string,
    newSessionCount: number,
  ) => void;
  updateUtilized: (params: any) => void;
}

export function UsageCard({
  bundle,
  user,
  id,
  updateUsageException,
  addFreeSession,
  updateUtilized,
}: UsageCardProps) {
  const [editingUserId, setEditingUserId] = useState(false);
  const [userIdValue, setUserIdValue] = useState(user.id ?? "");
  const [editingUsageCount, setEditingUsageCount] = useState<string | null>(
    null,
  );
  const [newUsageCount, setNewUsageCount] = useState<number>(
    bundle.utilized_session_quantity,
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingBundleId, setPendingBundleId] = useState<string | null>(null);

  const { data: bundlesData, isLoading: isBundlesLoading } = useActiveBundles();
  const { region = ALL_REGIONS_CODE } = useParams();

  // const handleSaveUserId = () => {
  //   onSaveUserId(bundle.id, userIdValue.trim());
  //   setEditingUserId(false);
  // };

  const handleBundleChange = (newBundleId: string) => {
    if (newBundleId !== bundle.bundle.id) {
      setPendingBundleId(newBundleId);
      setConfirmDialogOpen(true);
    }
  };

  const confirmBundleChange = () => {
    if (pendingBundleId) {
      updateBundleUsage({
        usage_id: bundle.id,
        new_bundle_id: pendingBundleId,
      });
      setPendingBundleId(null);
      setConfirmDialogOpen(false);
    }
  };
  const { mutate: updateUserId } = useUpdateUsageUser();
  const { mutate: updateBundleUsage } = useUpdateBundleUsage();

  const updateStatus = useUpdateBundleUsageStatus();
  const isMensHealth = isMensHealthBundle(bundle.bundle.standard_id);
  const isWomensHealth = isWomensHealthBundle(bundle.bundle.standard_id);
  const isTConsult = isMensHealthTconsultBundle(bundle.bundle.standard_id);
  const canCopyLinks = isUsageLinkCopyAllowed(bundle.status);
  const hasActiveFollowUp = bundle.sessions?.some(
    (s: any) =>
      s.name?.toLowerCase().includes('follow') &&
      (!s.status || s.status.toLowerCase() === 'active'),
  );
  const handleSaveUserId = () => {
    updateUserId(
      { source_id: bundle.id, user_id: userIdValue.trim() || null },
      {
        onSuccess: () => {
          toast({ title: "Updated usage identifier" });
          setEditingUserId(false);
        },
        onError: () => {
          toast({
            title: "Failed to update usage identifier",
            variant: "destructive",
          });
          setEditingUserId(false);
        },
      },
    );
  };

  return (
    <Card key={bundle.id} className="overflow-hidden">
      <CardHeader className="bg-gray-100 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              to={regionPath(region, `/sessions?usage_id=${bundle.id}`)}
              className="text-sidebar-primary hover:underline hover:text-text-sidebar-primary/80"
            >
              <CardTitle className="text-lg">{bundle.bundle.name}</CardTitle>
            </Link>
            {/* Expiration Warning Badge */}
            <BundleExpirationBadge
              endDate={bundle.end_date}
              utilizedSessions={bundle.utilized_session_quantity}
              totalSessions={bundle.total_session_quantity}
            />
          </div>
          <HasAccess permission="write">
            <div className="flex items-center gap-3">
              <BundleCombobox
                bundles={bundlesData?.bundles ?? []}
                value={bundle.bundle.id}
                fallbackLabel={bundle.bundle.name}
                onSelect={handleBundleChange}
                placeholder={
                  isBundlesLoading ? "Loading bundles..." : "Select bundle"
                }
                disabled={isBundlesLoading}
                triggerClassName="w-[250px]"
              />
              <div className="flex items-center gap-2">
                <Select
                  defaultValue={bundle.status}
                  onValueChange={(newStatus) =>
                    updateStatus.mutate({
                      bundle_id: bundle.id,
                      status: newStatus,
                    })
                  }
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue>
                      <Badge variant={bundleStatusToVariant(bundle.status)}>
                        {bundle.status}
                      </Badge>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                    <SelectItem value="EXPIRED">EXPIRED</SelectItem>
                    <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                    <SelectItem value="FULLY_REFUNDED">
                      FULLY_REFUNDED
                    </SelectItem>
                    <SelectItem value="COMPLETED_WITH_PARTIAL_REFUND">
                      COMPLETED_WITH_PARTIAL_REFUND
                    </SelectItem>
                    <SelectItem value="TRANSFERRED">TRANSFERRED</SelectItem>
                    <SelectItem value="CONVERTED">CONVERTED</SelectItem>
                  </SelectContent>
                </Select>
                {bundle.is_gifted && <Badge variant="gifted">GIFT</Badge>}
                {bundle.allow_schedule_exception && (
                  <Badge variant="destructive">GIVEN EXCEPTION</Badge>
                )}
              </div>
            </div>
            <Dialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Bundle Change</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  Are you sure you want to change this usage to a different
                  bundle?
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={confirmBundleChange}>
                    Confirm Change
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </HasAccess>
        </div>

        <div className="flex items-center justify-between">
          <CardDescription>
            {format(new Date(bundle.start_date), "PPP")} -{" "}
            {format(new Date(bundle.end_date), "PPP")}
          </CardDescription>
          <div className="flex items-center gap-2">
            <ExceptionBadge
              exceptionLogs={bundle.exceptionLogs}
              side="bottom"
            />
            <span>
              {bundle?.payment?.currency +
                " " +
                (bundle?.payment?.amount / 100).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Usage ID */}
        <div className="flex items-center gap-2">
          <span>usage id: {bundle.id}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(bundle.id);
                toast({ title: "Copied", description: "Usage ID copied" });
              } catch {
                toast({
                  title: "Error",
                  description: "Failed to copy Usage ID",
                  variant: "destructive",
                });
              }
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        {/* Editable User ID */}
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          User id:
          {editingUserId ? (
            <>
              <input
                className="border rounded px-1 text-sm"
                value={userIdValue}
                onChange={(e) => setUserIdValue(e.target.value)}
                autoFocus
              />
              <Button size="icon" variant="ghost" onClick={handleSaveUserId}>
                <Check className="w-4 h-4 text-green-600" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  setUserIdValue(user.id ?? "");
                  setEditingUserId(false);
                }}
              >
                <X className="w-4 h-4 text-red-600" />
              </Button>
            </>
          ) : (
            <>
              <span className="font-medium">{user.id || "—"}</span>
              <HasAccess permission="write">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setEditingUserId(true)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </HasAccess>
            </>
          )}
        </p>
      </CardHeader>

      <CardContent className="grid gap-4 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {!bundle?.source_usage_id && (
              <span className="font-sans text-lg pr-2">
                extra_session: {bundle.extra_reschedule_session}
              </span>
            )}
          </span>

          <div>
              <HasAccess permission="write">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* v2 booking link → dashboard product page for this usage.
                      Only when the v2 flag is on and the bundle has a product. */}
                  {canCopyLinks &&
                    isV2ProductsEnabled() &&
                    bundle.bundle?.product_id && (
                      <DropdownMenuItem
                        onClick={async () => {
                          if (!isUsageLinkCopyAllowed(bundle.status)) {
                            toast({
                              title: "Error",
                              description: getUsageLinkCopyNotAllowedMessage(
                                bundle.status,
                              ),
                              variant: "destructive",
                            });
                            return;
                          }
                          const link = buildV2ProductBookingLink(
                            bundle.bundle.product_id,
                            bundle.id,
                          );
                          await navigator.clipboard.writeText(link);
                          toast({
                            title: "Copied",
                            description: "V2 booking link copied to clipboard",
                          });
                        }}
                      >
                        Copy V2 Booking Link
                      </DropdownMenuItem>
                    )}

                  {canCopyLinks && isMensHealth && !hasActiveFollowUp && (
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!isUsageLinkCopyAllowed(bundle.status)) {
                          toast({
                            title: "Error",
                            description: getUsageLinkCopyNotAllowedMessage(
                              bundle.status,
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                        const links = getMensHealthFollowUpLinks({
                          usage_id: bundle.id,
                          user_country: user?.country,
                        });
                        await navigator.clipboard.writeText(links.free);
                        toast({
                          title: "Copied",
                          description:
                            "Free follow-up link copied to clipboard",
                        });
                      }}
                    >
                      Copy Free Follow-up Link
                    </DropdownMenuItem>
                  )}

                  {canCopyLinks && isMensHealth && (
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!isUsageLinkCopyAllowed(bundle.status)) {
                          toast({
                            title: "Error",
                            description: getUsageLinkCopyNotAllowedMessage(
                              bundle.status,
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                        const links = getMensHealthFollowUpLinks({
                          usage_id: bundle.id,
                          user_country: user?.country,
                        });
                        await navigator.clipboard.writeText(links.paid);
                        toast({
                          title: "Copied",
                          description:
                            "Paid follow-up link copied to clipboard",
                        });
                      }}
                    >
                      Copy Paid Follow-up Link
                    </DropdownMenuItem>
                  )}

                  {canCopyLinks && isWomensHealth && !hasActiveFollowUp && (
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!isUsageLinkCopyAllowed(bundle.status)) {
                          toast({
                            title: "Error",
                            description: getUsageLinkCopyNotAllowedMessage(
                              bundle.status,
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                        const links = getWomensHealthFollowUpLinks({
                          usage_id: bundle.id,
                        });
                        await navigator.clipboard.writeText(links.free);
                        toast({
                          title: "Copied",
                          description:
                            "Free follow-up link copied to clipboard",
                        });
                      }}
                    >
                      Copy Free Follow-up Link
                    </DropdownMenuItem>
                  )}

                  {canCopyLinks && isWomensHealth && (
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!isUsageLinkCopyAllowed(bundle.status)) {
                          toast({
                            title: "Error",
                            description: getUsageLinkCopyNotAllowedMessage(
                              bundle.status,
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                        const links = getWomensHealthFollowUpLinks({
                          usage_id: bundle.id,
                        });
                        await navigator.clipboard.writeText(links.paid);
                        toast({
                          title: "Copied",
                          description:
                            "Paid follow-up link copied to clipboard",
                        });
                      }}
                    >
                      Copy Paid Follow-up Link
                    </DropdownMenuItem>
                  )}

                  {canCopyLinks && isTConsult && (
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!isUsageLinkCopyAllowed(bundle.status)) {
                          toast({
                            title: "Error",
                            description: getUsageLinkCopyNotAllowedMessage(
                              bundle.status,
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                        const therapyLink = getMensHealthTherapyLink({
                          standard_id: bundle.bundle.standard_id,
                          usage_id: bundle.id,
                          sessions: bundle?.sessions,
                        });
                        if (!therapyLink) {
                          toast({
                            title: "Error",
                            description: "Therapy link not available",
                            variant: "destructive",
                          });
                          return;
                        }
                        await navigator.clipboard.writeText(therapyLink);
                        toast({
                          title: "Copied",
                          description: "Therapy link copied to clipboard",
                        });
                      }}
                    >
                      Copy Therapy Link
                    </DropdownMenuItem>
                  )}

                  {canCopyLinks && (
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!isUsageLinkCopyAllowed(bundle.status)) {
                          toast({
                            title: "Error",
                            description: getUsageLinkCopyNotAllowedMessage(
                              bundle.status,
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                        const schedule_link = getExceptionScheduleLink({
                          standard_id: bundle.bundle.standard_id,
                          usage_id: bundle.id,
                          session_event_type: bundle?.sessions?.[0]?.event_type,
                        });
                        await navigator.clipboard.writeText(schedule_link);
                      }}
                    >
                      Copy Exception Schedule Link
                    </DropdownMenuItem>
                  )}

                  {/* Copy links above stay for anyone who can view the user; everything
                      below changes the bundle, so it needs Edit. */}
                  <HasAccess permission="write">
                  <DropdownMenuItem
                    onClick={() => updateUsageException(bundle.id)}
                  >
                    {bundle.allow_schedule_exception
                      ? "Disable Exception"
                      : "Allow Exception"}
                  </DropdownMenuItem>

                  <ExtendBundleDialog
                    bundleId={bundle.id}
                    userId={id!}
                    endDate={bundle.end_date}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Extend bundle expiry
                      </DropdownMenuItem>
                    }
                  />

                  <DropdownMenuItem
                    onClick={() =>
                      addFreeSession(
                        bundle.id,
                        id,
                        bundle.total_session_quantity + 1,
                      )
                    }
                  >
                    Add free session
                  </DropdownMenuItem>

                  {/* ADHD assessment care-team bridge. Each item is gated by the
                      bundle's care_team_actions flags (set in the bundle wizard):
                      two-session pack → outcome only, standalone DIVA → report
                      only, full pack → both. Absent → hidden (non-ADHD bundles). */}
                  {bundle.bundle?.metadata?.care_team_actions
                    ?.assessment_outcome && (
                    <AssessmentOutcomeDialog
                      usageId={bundle.id}
                      currentOutcome={bundle.metadata?.assessment_outcome}
                      currentNotes={bundle.metadata?.assessment_notes}
                      trigger={
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          Record exploration outcome
                        </DropdownMenuItem>
                      }
                    />
                  )}
                  {bundle.bundle?.metadata?.care_team_actions?.diva_report && (
                    <DivaReportDialog
                      usageId={bundle.id}
                      reportSaved={!!bundle.metadata?.diva_report_key}
                      currentOutcome={bundle.metadata?.diva_outcome}
                      currentScores={bundle.metadata?.diva_scores}
                      reportType={
                        bundle.bundle?.metadata?.care_team_actions?.report_type
                      }
                      trigger={
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          {bundle.bundle?.metadata?.care_team_actions
                            ?.report_type === "ipde"
                            ? "IPDE report"
                            : "DIVA report"}
                        </DropdownMenuItem>
                      }
                    />
                  )}
                  </HasAccess>
                </DropdownMenuContent>
              </DropdownMenu>
              </HasAccess>

            {/* Usage Count Editable */}
            <div className="flex items-center gap-2 mt-2">
              {editingUsageCount === bundle.id ? (
                <>
                  <input
                    type="number"
                    className="w-16 border rounded px-1 text-sm"
                    value={newUsageCount}
                    onChange={(e) => setNewUsageCount(Number(e.target.value))}
                    autoFocus
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      updateUtilized({
                        usage_id: bundle.id,
                        new_utilization_session__count: newUsageCount,
                      });
                      setEditingUsageCount(null);
                    }}
                  >
                    <Check className="w-4 h-4 text-green-600" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditingUsageCount(null)}
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="font-semibold text-xl">
                    {bundle.utilized_session_quantity +
                      bundle.extra_exception_session}{" "}
                    / {bundle.total_session_quantity}
                  </span>
                  <HasAccess permission="write">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setNewUsageCount(bundle.utilized_session_quantity);
                        setEditingUsageCount(bundle.id);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </HasAccess>
                </>
              )}
            </div>
          </div>
        </div>

        {bundle.is_gifted && bundle.gifter && (
          <GiftedByInfo
            gifter={bundle.gifter}
            messageFromGifter={bundle.message_from_gifter}
          />
        )}
      </CardContent>
    </Card>
  );
}
